import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, ChevronLeft } from "lucide-react";
import useTranslation from "../client/hooks/useTranslation";

interface StudentPermissions {
  canViewJournal: boolean;
  canViewDashboard: boolean;
  canViewAnalytics: boolean;
  canViewSessions: boolean;
  canViewPropFirmManager: boolean;
}

interface StudentLayoutProps {
  studentId: string;
  studentFirstName: string | null;
  studentLastName: string | null;
  studentEmail?: string;
  permissions: StudentPermissions;
  children: ReactNode;
}

// studentId and permissions are no longer read here (the sidebar owns the
// per-student navigation now) but stay in the contract: every caller passes them,
// and they are what a future in-layout affordance would need.
export default function StudentLayout({
  studentFirstName,
  studentLastName,
  studentEmail,
  children,
}: StudentLayoutProps) {
  const t = useTranslation();
  const navigate = useNavigate();

  const displayName =
    [studentFirstName, studentLastName].filter(Boolean).join(" ") ||
    studentEmail ||
    "";

  return (
    <div className="min-h-screen bg-background">
      {/* Banner — même style que le CTA free plan */}
      <div className="mb-6 rounded-lg border border-primary/50 bg-gradient-to-r from-primary/5 via-background to-primary/5 px-6 py-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="rounded-full bg-primary/10 p-3 shrink-0">
              <Eye className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">
                {t.mentor.readOnlyMode}
              </p>
              {displayName && (
                <p className="text-sm text-muted-foreground mt-0.5">
                  {t.mentor.studentView} : <span className="font-medium text-foreground">{displayName}</span>
                </p>
              )}
            </div>
          </div>
          <button
            onClick={() => navigate("/mentor")}
            className="shrink-0 flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
            {t.mentor.backToStudents}
          </button>
        </div>
      </div>

      {/* The per-student navigation lives in the secondary sidebar (navConfig
          buildStudentEntries) — the horizontal tab bar that used to sit here
          duplicated it. */}
      <main>{children}</main>
    </div>
  );
}
